import React, { useRef } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { CaseAttachmentFileRules, caseAttachmentInitialData } from 'app/edozvola/cases/case-attachment/model/case-attachment.model';
import { caseAttachmentSchema } from 'app/edozvola/cases/case-attachment/model/case-attachment.schema';
import {
  fetchCaseAttachmentQuery,
  getCaseAttachmentFileRulesMutation,
  updateCaseAttachmentQuery,
} from 'app/edozvola/cases/case-attachment/service/case-attachment.queries';
import { Loader } from 'app/shared/loader/loader';
import { Button, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadCaseAttachmentFile, getCaseAttachmentFile } from 'app/edozvola/cases/case-attachment/service/case-attachment.service';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { APP_DATE_FORMAT, MAX_FILE_NAME_LENGTH, MAX_FILE_SIZE, messages } from 'app/config/constants';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { fetchRequestAttachmentsForCaseCategoryQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { openFileInNewWindow } from 'app/shared/util/navigation-util';
import error = toast.error;
import { downloadFile } from 'app/shared/util/file-utils';
import FileUpload from 'app/shared/file-upload/file-upload';

export interface ICaseAttachmentAddForm {
  idCaseAttachment: string;
  setIdCaseAttachment: any;
  readOnly: boolean;
}

const CaseAttachmentAddForm = (props: ICaseAttachmentAddForm) => {
  const { id: uuid } = useParams<{ id: string }>();
  const hiddenFileInput = useRef(null);

  const { data, isLoading } = fetchCaseAttachmentQuery(uuid, props.idCaseAttachment);
  const { data: requestAttachmentOptions, isLoading: codebookLoading } = fetchRequestAttachmentsForCaseCategoryQuery(uuid);

  const { mutate: getCaseAttachmentFileRules, isPending: caseAttachmentFileRulesPending } = getCaseAttachmentFileRulesMutation(
    (fileRules: CaseAttachmentFileRules) => {
      formik.setValues({
        ...formik.values,
        canUpload: fileRules.canUpload,
        dataFormats: fileRules.dataFormats,
      });
    },
  );

  const { mutate: updateCaseAttachment, isPending } = updateCaseAttachmentQuery(
    uuid,
    () => {},
    () => closeModal(),
  );

  const formik = useFormik({
    initialValues: data || caseAttachmentInitialData,
    onSubmit: values => updateCaseAttachment(values),
    validationSchema: caseAttachmentSchema,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const extractFileNameWithoutExtension = (fileName: string, maxLength = MAX_FILE_NAME_LENGTH): string => {
    if (!fileName) return '';

    const dotIndex = fileName.lastIndexOf('.');
    const nameWithoutExtension = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;

    return nameWithoutExtension.length > maxLength ? nameWithoutExtension.substring(0, maxLength) : nameWithoutExtension;
  };

  const previewFile = () => {
    if (formik.values.fileWrapper) {
      getCaseAttachmentFile(formik.values.idCaseAttachment)
        .then(data => {
          if (data) {
            openFileInNewWindow(data.url);
          }
        })
        .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'));
    }
  };

  const downloadCaseAttachment = () => {
    downloadCaseAttachmentFile(formik.values.idCaseAttachment)
      .then(data => {
        if (data) {
          const applicationType = data.fileType.type + '/' + data.fileType.subtype;
          downloadFile(formik.values.fileWrapper.fileName, data.contentData, applicationType);
        }
      })
      .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'));
  };

  const closeModal = () => {
    props.setIdCaseAttachment(null);
    formik.resetForm({ values: caseAttachmentInitialData });
  };

  const onRequestAttachmentChanged = options => {
    formik.setValues({
      ...caseAttachmentInitialData,
      idCaseAttachment: formik.values.idCaseAttachment,
      requestAttachment: options,
    });
    getCaseAttachmentFileRules(options[0].id);
  };

  const attachmentHasError = () => {
    return !props.readOnly && formik.values.canUpload && !formik.values.multipartFile && !formik.values.fileWrapper;
  };

  return (
    <Modal scrollable size={'xl'} isOpen={props.idCaseAttachment !== null}>
      <ModalHeader>
        {props.idCaseAttachment === '' ? 'Unos priloga' : props.readOnly ? 'Pregled priloga' : 'Uređivanje priloga'}
      </ModalHeader>
      <ModalBody>
        <FormikProvider value={formik}>
          {(isLoading || codebookLoading || isPending) && <Loader />}
          {!isLoading && !codebookLoading && (
            <div className={'d-flex flex-column gap-1'}>
              <Row>
                <Col md={12}>
                  <Label labelText={'Vrsta priloga'} />
                  <PaginatedDropdown
                    placeholder={!props?.readOnly && 'Odaberi vrstu priloga'}
                    onItemSelect={onRequestAttachmentChanged}
                    selected={formik.values.requestAttachment}
                    labelKey={'value'}
                    options={requestAttachmentOptions}
                    id="requestAttachment"
                    disabled={props.readOnly}
                    positionFixed
                    onBlur={formik.handleBlur}
                    error={!props.readOnly && ((formik.errors.requestAttachment as string) ?? '')}
                    showErrorMessage
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label labelText={'Prilog'} />
                  <FileUpload
                    disabled={props.readOnly || (formik.values.canUpload !== undefined && !formik.values.canUpload)}
                    allowedExtensions={formik.values.dataFormats?.length > 0 ? formik.values.dataFormats : ['pdf']}
                    value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName ?? ''}
                    error={attachmentHasError() ? messages.MANDATORY_FIELD : ''}
                    clearButton={false}
                    setFile={selectedFile => {
                      formik.setFieldValue('multipartFile', selectedFile);
                      formik.setFieldValue('fileWrapper.changed', true);
                      formik.setFieldValue('name', selectedFile ? extractFileNameWithoutExtension(selectedFile.name) : '');
                    }}
                    appendIcon={formik.values.fileWrapper && formik.values.idCaseAttachment ? 'download' : 'paperclip'}
                    appendOnClick={
                      formik.values.fileWrapper && formik.values.idCaseAttachment
                        ? formik.values.fileWrapper.fileName?.includes('pdf')
                          ? previewFile
                          : downloadCaseAttachment
                        : undefined
                    }
                  />
                </Col>
                <Col md={6}>
                  <Label labelText={'Naziv priloga'} />
                  <Input
                    disabled={props.readOnly || (!formik.values.fileWrapper && !formik.values.multipartFile)}
                    id="name"
                    name="name"
                    maxLength={60}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    error={!props.readOnly && formik.errors.name ? formik.errors.name : ''}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label labelText={'Datum unosa'} />
                  <Field
                    id="createdDate"
                    name="createdDate"
                    render={({ field }) => (
                      <FormikDatepicker
                        field={field}
                        setFieldValue={formik.setFieldValue}
                        dateFormat={APP_DATE_FORMAT}
                        error={formik.errors.createdDate ? `${formik.errors.createdDate}` : ''}
                        disabled={true}
                      />
                    )}
                  />
                </Col>
              </Row>
            </div>
          )}
        </FormikProvider>
      </ModalBody>
      <ModalFooter>
        <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
        {!props.readOnly && (
          <Button
            label={'Spremi'}
            size={'lg'}
            disabled={!formik.dirty || !formik.isValid || attachmentHasError()}
            isLoading={isPending}
            color={'info'}
            onClick={() => {
              formik.handleSubmit();
            }}
          />
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CaseAttachmentAddForm;
